import { makeAutoObservable, runInAction } from "mobx";
import localesListStore, { LocalesListStore } from "./locales-list-store";
import { IOption } from "../../../components/inputs/select-dropdown-multi/select-dropdown-multi";
import { isSet } from "../../../models/model-types-methods";

export enum ADD_LANGUAGE_WIZARD_STEPS {
    SELECT_LANGUAGE = 'SELECT_LANGUAGE',
    DOWNLOAD_CSV = 'DOWNLOAD_CSV',
    UPLOAD_CSV = 'UPLOAD_CSV',
};

export class AddLanguageWizardStore {
    private _steps: ADD_LANGUAGE_WIZARD_STEPS[] = [];
    private _currentStep: ADD_LANGUAGE_WIZARD_STEPS = ADD_LANGUAGE_WIZARD_STEPS.SELECT_LANGUAGE;
    private _localeListStore: LocalesListStore;
    private _selectedLocalesOptions?: IOption[];
    private _isAddingLocales: boolean = false;

    private _isUploadingCSV: boolean = false;
    private _uploadFile?: File;
    private _uploadFileContent?: string | ArrayBuffer | null | undefined;

    constructor(localeListStore: LocalesListStore) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._localeListStore = localeListStore;
        this._steps = [
            ADD_LANGUAGE_WIZARD_STEPS.SELECT_LANGUAGE,
            ADD_LANGUAGE_WIZARD_STEPS.DOWNLOAD_CSV,
            ADD_LANGUAGE_WIZARD_STEPS.UPLOAD_CSV,
        ];
    }

    public get isUploadingTranslationsFile(): boolean { return this._isUploadingCSV; }
    public get translationsFilename(): string | undefined { return this._uploadFile?.name; }
    public get selectedLocalesOptions(): IOption[] | undefined { return this._selectedLocalesOptions; }
    public get currentStep(): ADD_LANGUAGE_WIZARD_STEPS { return this._currentStep; }
    public get currentStepIndex(): number { return this.steps.findIndex((step) => step === this.currentStep); }
    public get steps(): ADD_LANGUAGE_WIZARD_STEPS[] { return this._steps; }
    public get hasNextStep(): boolean { return this.currentStepIndex < this.steps.length - 1; }
    public get isAddingLocales(): boolean { return this._isAddingLocales; }
    
    public get canMoveToNextStep(): boolean {
        switch (this._currentStep) {
            case ADD_LANGUAGE_WIZARD_STEPS.SELECT_LANGUAGE:
                if (!isSet(this.selectedLocalesOptions)) {
                    return false;
                }
                return true;
            case ADD_LANGUAGE_WIZARD_STEPS.DOWNLOAD_CSV:
                return true;
            case ADD_LANGUAGE_WIZARD_STEPS.UPLOAD_CSV:
                return isSet(this._uploadFile) && isSet(this._uploadFileContent);
        }

        return false;
    }

    public get cancelButtonTranslation(): string {
        if (this.currentStep === ADD_LANGUAGE_WIZARD_STEPS.UPLOAD_CSV) {
            return 'Upload later';
        }
        return 'Cancel';
    }

    public get submitButtonTranslation(): string {
        if (this.currentStep === ADD_LANGUAGE_WIZARD_STEPS.UPLOAD_CSV) {
            return 'Apply';
        }
        return 'Next';
    }

    public get localesOptions(): IOption[] | undefined {
        return this._localeListStore.getAllLocales?.map((localeItem): IOption => ({
            value: localeItem.value,
            label: localeItem.label,
            disabled: !!this._localeListStore.localesList.find((option) => option.localeCode === localeItem.value),
        }));
    }

    private async _handleCurrentStepAction(): Promise<boolean> {
        switch (this.currentStep) {
            case ADD_LANGUAGE_WIZARD_STEPS.SELECT_LANGUAGE:
                return this._handleLanguageSelection();
            case ADD_LANGUAGE_WIZARD_STEPS.UPLOAD_CSV:
                return this._handleFileUpload();
        }
        return Promise.resolve(true);
    }

    private async _handleLanguageSelection(): Promise<boolean> {
        if (!this._selectedLocalesOptions?.length) {
            return false;
        }
        
        const locales = this._selectedLocalesOptions.map(option => `${option.value}`);
        
        this._isAddingLocales = true;        
        const isSucceeded = await localesListStore.handleAddLocales(locales);
        
        runInAction(() => {
            this._isAddingLocales = false;
        });

        return isSucceeded;
    }

    private async _handleFileUpload(): Promise<boolean> {
        if (!this._uploadFile || !this._uploadFileContent) {
            return false;
        }
        
        this._isUploadingCSV = true;
        const isSucceeded = await this._localeListStore.uploadTranslationsFile(this._uploadFile, this._uploadFileContent);

        runInAction(() => {
            this._isUploadingCSV = false;
            this._localeListStore.setIsAddLanguageWizardOpen(!isSucceeded);
        });
        return isSucceeded;
    }

    public setSelectedLocalesOptions(options: IOption[]) {
        this._selectedLocalesOptions = options;
    }

    public uploadTranslationsFile(file: File, fileContent: string | ArrayBuffer | null | undefined) {
        this._uploadFile = file;
        this._uploadFileContent = fileContent;
    }

    public async nextStep(): Promise<void> {
        if (!this.canMoveToNextStep) {
            return;
        }

        const isSucceeded = await this._handleCurrentStepAction();
        
        if (isSucceeded && this.hasNextStep) {
            runInAction(() => {
                this._currentStep = this.steps[this.currentStepIndex + 1];
            });
        }
    }
}

export default AddLanguageWizardStore;